import React from "react";
import Reveal from "@/components/Reveal";
import { BookButton, OrderButton } from "@/components/ActionButtons";
import { IMG, RESTAURANT } from "@/data/restaurant";
import { Phone, Mail, MapPin, Clock, CheckCircle2, MessageSquare } from "lucide-react";

/**
 * Reservations landing page — Infuse is partnered with OpenTable.
 * All bookings, confirmations, and reminders are handled by OpenTable.
 */
export default function Reservations() {
  return (
    <div data-testid="reservations-page" className="pt-28 bg-[var(--infuse-cream)] min-h-screen">
      {/* Hero strip */}
      <section className="relative h-[44vh] min-h-[340px] overflow-hidden grain">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(6,30,25,0.55) 0%, rgba(6,30,25,0.85) 100%), url(${IMG.interior2})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 h-full flex flex-col justify-end pb-14">
          <span className="gold-divider font-eyebrow text-[var(--infuse-gold-soft)]">Reservations</span>
          <h1 className="font-display text-6xl md:text-7xl text-white mt-4 leading-[0.95]">
            Reserve your table.
          </h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-12">
        {/* Main CTA card */}
        <Reveal className="lg:col-span-7">
          <div className="bg-white p-10 md:p-14 border border-[var(--infuse-gold)]/30">
            <span className="gold-divider font-eyebrow text-[var(--infuse-gold)]">Powered by OpenTable</span>
            <h2 className="font-display text-4xl md:text-5xl mt-5 text-[var(--infuse-emerald)] leading-tight">
              Book in seconds. Confirmed instantly.
            </h2>
            <p className="mt-6 text-[var(--infuse-ink)]/70 leading-relaxed text-lg">
              We've partnered with OpenTable so you can secure your table with one tap. You'll receive an
              immediate <span className="text-[var(--infuse-emerald)] font-medium">email confirmation</span> and a
              <span className="text-[var(--infuse-emerald)] font-medium"> text reminder</span> ahead of your visit.
            </p>

            <ul className="mt-8 space-y-3 text-[var(--infuse-ink)]/85">
              {[
                "Real-time availability — book up to 90 days ahead",
                "Email + SMS reminders sent automatically",
                "Modify or cancel anytime from your confirmation",
                "Add a special occasion or dietary note at checkout",
              ].map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <CheckCircle2 size={18} strokeWidth={1.6} className="text-[var(--infuse-pomegranate)] mt-0.5 shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-wrap gap-3">
              <BookButton testid="resv-book-cta">Book a Table on OpenTable</BookButton>
              <OrderButton testid="resv-order-cta">Order Online Instead</OrderButton>
            </div>
            <p className="mt-6 text-sm text-[var(--infuse-ink)]/60 flex items-start gap-2">
              <MessageSquare size={14} className="mt-0.5 text-[var(--infuse-gold)]" />
              For parties over 8 or private dining, please call us on <a href={RESTAURANT.phoneHref} className="text-[var(--infuse-emerald)] underline underline-offset-2 hover:text-[var(--infuse-pomegranate)]">{RESTAURANT.phone}</a>.
            </p>
          </div>
        </Reveal>

        {/* Aside */}
        <Reveal className="lg:col-span-5" delay={120}>
          <div className="bg-[var(--infuse-emerald)] text-white p-10 relative overflow-hidden">
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${IMG.emeraldBg})`, backgroundSize: "cover" }} />
            <div className="relative">
              <span className="gold-divider font-eyebrow text-[var(--infuse-gold-soft)]">Prefer a human voice?</span>
              <h3 className="font-display text-3xl mt-4">We're here to help.</h3>
              <p className="mt-3 text-white/75 leading-relaxed">
                Large parties, private events, or special arrangements — call or email our team directly.
              </p>
              <div className="mt-6 space-y-3 text-white/85">
                <p className="flex items-center gap-3">
                  <Phone size={18} className="text-[var(--infuse-gold-soft)]" />
                  <a href={RESTAURANT.phoneHref} className="hover:text-[var(--infuse-gold-soft)]">{RESTAURANT.phone}</a>
                </p>
                <p className="flex items-center gap-3">
                  <Mail size={18} className="text-[var(--infuse-gold-soft)]" />
                  <a href={`mailto:${RESTAURANT.email}`} className="hover:text-[var(--infuse-gold-soft)]">{RESTAURANT.email}</a>
                </p>
                <p className="flex items-start gap-3">
                  <MapPin size={18} className="mt-0.5 text-[var(--infuse-gold-soft)]" /> {RESTAURANT.address}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-[var(--infuse-gold)]/30 p-10 mt-6">
            <div className="flex items-center gap-2">
              <Clock size={18} className="text-[var(--infuse-gold)]" />
              <h4 className="font-eyebrow text-[var(--infuse-gold)]">Opening Hours</h4>
            </div>
            <div className="mt-4 grid grid-cols-[110px_1fr] gap-y-1.5 text-sm">
              {RESTAURANT.hours.map((h) => (
                <React.Fragment key={h.day}>
                  <span className="font-eyebrow text-[0.62rem] text-[var(--infuse-ink)]/55">{h.day}</span>
                  <span className="text-[var(--infuse-ink)]/85">{h.time}</span>
                </React.Fragment>
              ))}
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
